<template>
    <div>
        <DatabaseRequestView @update="update" v-show="true" :dashboard="true" :show_params="show_params" :minimal="true" :test_jwt="test_jwt" ref="view" />

        <p v-if="intro" v-html="intro"></p>

        <!-- Toggle tableau / graphique (array avec option chart) -->
        <div v-if="show_chart_toggle" class="db-view-toggle">
            <b-button-group size="sm">
                <b-button :variant="chart_view === 'table' ? 'primary' : 'outline-secondary'" @click="switchView('table')">
                    <i class="fas fa-table"></i> Tableau
                </b-button>
                <b-button :variant="chart_view === 'chart' ? 'primary' : 'outline-secondary'" @click="switchView('chart')">
                    <i class="fas fa-chart-pie"></i> Graphique
                </b-button>
            </b-button-group>
        </div>

        <div v-show="!type">
            <p v-if="data.length > 0 && view_nb_result">Nombre de résultat{{ data.length > 1 ? "s": ""}} : {{ data.length}} <span v-if="limit && data.length >= limit">(limite de résultats atteinte)</span></p>
            <!-- Le tabulator ne doit JAMAIS être dans un display:none lors de l'init -->
            <div :class="{ 'db-hidden-no-display': show_chart_toggle && chart_view === 'chart' }">
                <edit_tabulator :minimal="minimal_tabulator" v-show="data.length > 0" :extern_file="false" ref="tabulator"></edit_tabulator>
            </div>
            <div v-if="show_chart_toggle && chart_view === 'chart' && data.length > 0">
                <template v-if="chart_configs.length > 1">
                    <b-tabs content-class="pt-2">
                        <b-tab v-for="(cfg, i) in chart_configs" :key="i"
                            :title="cfg.title || `Graphique ${i + 1}`">
                            <chart-display :rows="data" :config="cfg" :editable="true" />
                        </b-tab>
                    </b-tabs>
                </template>
                <chart-display v-else-if="chart_configs.length === 1"
                    :rows="data" :config="chart_configs[0]" :editable="true" />
            </div>
        </div>
        <div v-if="type == 'array_conditions'">
            <p v-if="data.length > 0 && view_nb_result">Nombre de résultat{{ size > 1 ? "s": ""}} au total : {{ size}} <span v-if="limit && data.length >= limit">(limite de résultats atteinte)</span></p>
            <edit_tabulator_conditions @update="set_size" :data="data" :minimal="minimal_tabulator" v-show="data.length > 0" :extern_file="false" ref="tabulator_conditions"></edit_tabulator_conditions>
        </div>
        <div v-if="type == 'nb_lines'" style="text-align:center">
            <div style="font-size:5em">{{number}}</div>
            <div v-if="description">{{description}}</div>
        </div>
        <!-- Graphiques dédiés chart_pie / chart_line -->
        <div v-if="type === 'chart_pie' || type === 'chart_line'">
            <template v-if="chart_configs.length > 1">
                <b-tabs content-class="pt-2">
                    <b-tab v-for="(cfg, i) in chart_configs" :key="i"
                        :title="cfg.title || `Graphique ${i + 1}`">
                        <chart-display :rows="data" :config="cfg" :editable="true" />
                    </b-tab>
                </b-tabs>
            </template>
            <chart-display v-else-if="chart_configs.length === 1"
                :rows="data" :config="chart_configs[0]" :editable="true" />
        </div>

        <div v-if="type == 'list'">
            <div v-for="(line, idx) in data" :key="idx">
                <div v-html="line.text"></div>
            </div>
        </div>
        <div v-if="type == 'calendar'">
            <v-calendar 
                :columns="columns"
                :rows="rows"
                class="custom-calendar max-w-full" 
                :attributes="data" 
                :masks="
                {
                    weekdays: 'WWW'
                }"
                disable-page-swipe
                is-expanded >
                 <template v-slot:day-content="{ day, attributes }">
                    <div class="flex flex-col h-full z-10 overflow-hidden">
                        <span class="day-label text-sm text-gray-900">{{ day.day }}</span>
                        <div class="flex-grow overflow-y-auto overflow-x-auto">
                            <p
                            v-b-tooltip.hover :title="attr.customData.description"
                            v-for="attr in attributes"
                            :key="attr.key"
                            class="text-xs leading-tight rounded-sm p-1 mt-0 mb-1"
                            :class="attr.customData.class"
                            >
                            {{ attr.customData.title }}
                            </p>
                        </div>
                    </div>
                </template>
            </v-calendar>
        </div>
    </div>

</template>

<script>

    import edit_tabulator_conditions from "../edit_tabulator_conditions.vue";
    import DatabaseRequestView from "../../views/DatabaseRequestView.vue";
    import edit_tabulator from "../edit_tabulator.vue";
    import ChartDisplay from "../ChartDisplay.vue";

    import _ from 'lodash';
    import moment from "moment";
    import VCalendar from 'v-calendar';

    require('moment/locale/fr')

    // Helper : normalise les configs graphiques quel que soit le format sauvegardé
    function getCharts(p, defaultType = 'pie') {
        if (p.charts && p.charts.length > 0) return p.charts;
        if (p.chart && typeof p.chart === 'object') {
            const c = p.chart;
            return [{
                type:            c.type            || defaultType,
                data_mode:       c.data_mode       || 'count',
                label_col:       c.label_col       || '',
                date_trunc:      c.date_trunc      || false,
                date_trunc_unit: c.date_trunc_unit || 'day',
                value_col:       c.value_col       || '',
                series_col:      c.series_col      || '',
                title:           c.title           || ''
            }];
        }
        // Très ancien format : champs chart_ à plat dans params
        if (p.chart_data_mode || p.chart_label_col) {
            return [{
                type:            p.chart_type            || defaultType,
                data_mode:       p.chart_data_mode       || 'count',
                label_col:       p.chart_label_col       || '',
                date_trunc:      p.chart_date_trunc      || false,
                date_trunc_unit: p.chart_date_trunc_unit || 'day',
                value_col:       p.chart_value_col       || '',
                series_col:      p.chart_series_col      || '',
                title:           p.chart_title           || ''
            }];
        }
        return [];
    }

    export default {
		components: {
            edit_tabulator_conditions,
            DatabaseRequestView,
            edit_tabulator,
            ChartDisplay
		},
        props: {
            format: {
                default: "small"
            },
            init_params: {
            },
            tooltip: {},
            test_jwt: {
                default: null
            }
        },
        data: function () {
            return {
                type: null,
                data: [],
                number: null,
                description: null,
                view_nb_result: false,
                intro: "",
                minimal_tabulator: false,
                rows: 1,
                clumns: 1,
                limit: 0,
                show_params: false,
                params: null,
                size: 0,
                // Chart toggle
                show_chart_toggle: false,
                chart_view: 'table',
                chart_configs: []
            };
		},
		methods: {
            switchView(view) {
                this.chart_view = view;
            },
            set_size(data)
            {
                this.size = data.length
            },
            async update(data)
            {
                if (typeof data == 'function')
                    return;

                this.data = data;
                if (this.type == "nb_lines")
                {
                    var data_view = data;
                    if (data_view.length > 1)
                        this.number = data_view.length;
                    else
                        this.number = data_view[0].count;
                    if (this.number == 0 || this.number == 1)
                        this.description = this.params.text_s;
                    else
                        this.description = this.params.text_p;
                }
                else if (this.type == "list")
                {
                    this.data = data;
                    var compiled = _.template(this.params.template);
                    console.log(this.data)
                    for (var i = 0; i < this.data.length; i++)
                    {
                        this.data[i].text = compiled({ data: this.data[i], moment});
                    }
                }
                else if (this.type == "calendar")
                {
                    this.rows = this.params.rows || 1;
                    this.columns = this.params.columns || 1;
                    this.type = "calendar";
                    await this.$refs.view.test(false);
                    this.data = data;
                    var compiled = _.template(this.params.template);
                    var compiled_desc = _.template(this.params.template_desc);
                    for (var i = 0; i < this.data.length; i++)
                    {
                        this.data[i].text = compiled({ data: this.data[i], moment });
                        this.data[i].desc = compiled_desc({ data: this.data[i], moment });
                        this.data[i] = {
                            key: i,
                            customData: {
                                title: this.data[i].text,
                                description: this.data[i].desc,
                                class: 'bg-blue-500 text-white',
                            },
                            dates: new Date(this.data[i][this.params.column_date]),
                        }
                    }
                }
                else if (this.type == "array_conditions")
                {
                    this.type = "array_conditions";

                    var keys = Object.keys((data && data.length > 0 && data[0]) ? data[0] : {});
                    for (var i = 0; i < data.length; i++)
                    {
                        for (var j = 0; j < keys.length; j++)
                        {
                            if (typeof data[i][keys[j]] == "number")
                                data[i][keys[j]] = data[i][keys[j]].toString();
                            if (typeof data[i][keys[j]] == "object")
                            {
                                if (!data[i][keys[j]])
                                    data[i][keys[j]] = "";
                                else
                                    data[i][keys[j]] = JSON.stringify(data[i][keys[j]]);
                            }
                        }
                    }
                
                    this.data = data;
                }
                else
                {
                    this.$refs.tabulator.set_data(data)
                    this.data = data;
                }

                this.size = this.data.length;
            },
            async save()
            {
                return {
                    params: await this.$refs.view.save(),
                    tparams: await this.$refs.view.save_params()
                }
            },
            async load(load_data)
            {
                var data = load_data

                if (typeof data.params == "string")
                    data.params = JSON.parse(data.params);

                await this.$refs.view.set_name(data.params.name);
                await this.$refs.view.load_params(data.tparams);

                this.limit = data.params.request.limit;
                
                this.number = null;
                this.type = null;

                var params = {};

                if (this.format == "small" && data.small)
                    params = data.small;

                if (this.format == "big" && data.big)
                    params = data.big;

                this.params = params;

                this.show_params = params.show_params || false;

                if (params.type == "nb_lines")
                {
                    this.type = "nb_lines";
                    await this.$refs.view.test(true);
                    var data_view = await this.$refs.view.get_data();
                    if (data_view.length > 1)
                        this.number = data_view.length;
                    else
                        this.number = data_view[0].count;
                    if (this.number == 0 || this.number == 1)
                        this.description = params.text_s;
                    else
                        this.description = params.text_p;
                }
                else if (params.type == "list")
                {
                    this.type = "list";
                    await this.$refs.view.test(false);
                    this.data = await this.$refs.view.get_data();
                    var compiled = _.template(params.template);
                    for (var i = 0; i < this.data.length; i++)
                    {
                        this.data[i].text = compiled({ data: this.data[i], moment});
                    }
                }
                else if (params.type == "calendar")
                {
                    this.rows = params.rows || 1;
                    this.columns = params.columns || 1;
                    this.type = "calendar";
                    await this.$refs.view.test(false);
                    this.data = await this.$refs.view.get_data();
                    var compiled = _.template(params.template);
                    var compiled_desc = _.template(params.template_desc);
                    for (var i = 0; i < this.data.length; i++)
                    {
                        this.data[i].text = compiled({ data: this.data[i], moment });
                        this.data[i].desc = compiled_desc({ data: this.data[i], moment });
                        this.data[i] = {
                            key: i,
                            customData: {
                                title: this.data[i].text,
                                description: this.data[i].desc,
                                class: 'bg-blue-500 text-white',
                            },
                            dates: new Date(this.data[i][params.column_date]),
                        }
                    }
                }
                else if (params.type == "array_conditions")
                {
                    this.type = "array_conditions";
                    this.view_nb_result = false;
                    if (params.view_nb_result)
                        this.view_nb_result = true;
                    await this.$refs.view.test(false);
                    var data = await this.$refs.view.get_data();
                    var keys = Object.keys((data && data.length > 0) ? data[0] : {});
                    for (var i = 0; i < data.length; i++)
                    {
                        for (var j = 0; j < keys.length; j++)
                        {
                            if (typeof data[i][keys[j]] == "number")
                                data[i][keys[j]] = data[i][keys[j]].toString();
                            if (typeof data[i][keys[j]] == "object")
                            {
                                if (!data[i][keys[j]])
                                    data[i][keys[j]] = "";
                                else
                                    data[i][keys[j]] = JSON.stringify(data[i][keys[j]]);
                            }
                        }
                    }
                    this.data = data;
                }
                else if (params.type === 'chart_pie' || params.type === 'chart_line')
                {
                    this.type = params.type;
                    this.chart_configs = getCharts(params, params.type === 'chart_line' ? 'line' : 'pie');
                    await this.$refs.view.test(false);
                    this.data = await this.$refs.view.get_data();
                }
                else
                {
                    this.view_nb_result = false;
                    if (params.view_nb_result)
                        this.view_nb_result = true;

                    // Chart toggle pour les types array
                    this.show_chart_toggle = !!params.allow_chart_toggle;
                    this.chart_view = params.chart_default_view || (params.chart && params.chart.default_view) || 'table';
                    this.chart_configs = getCharts(params, 'pie');

                    await this.$refs.view.test(false);
                    this.data = await this.$refs.view.get_data();
                    this.$refs.tabulator.set_data(this.data);
                }

                this.size = this.data.length;

                this.intro = "";
                if (params.description)
                    this.intro = params.description;

                this.minimal_tabulator = "";
                if (params.minimal_tabulator)
                    this.minimal_tabulator = params.minimal_tabulator;

                if (this.tooltip)
                {
                    window.scrollBy(0,1);
                    window.scrollBy(0,-1);
                }
            }
		},
		mounted: function () {
            if (this.init_params)
                this.load(this.init_params);
		},
    }


</script>

<style>

/* Masque visuellement sans display:none pour ne pas casser Tabulator.js */
.db-hidden-no-display {
    visibility: hidden;
    height: 0;
    overflow: hidden;
}
.db-view-toggle {
    margin-bottom: 10px;
}

.custom-calendar {
  --day-border: 1px solid #b8c2cc;
  --day-border-highlight: 1px solid #b8c2cc;
  --day-width: 90px;
  --day-height: 90px;
  --weekday-bg: #f8fafc;
  --weekday-border: 1px solid #eaeaea;
  border-radius: 1;
  width: 100%;
}
.vc-header {
    background-color: #f1f5f8;
    padding: 10px 0;
  }
  
.vc-weeks {
    padding: 0;
  }

.vc-weekday {
    background-color: #f8fafc;
    border-bottom: 1px solid #eaeaea;
    border-top: 1px solid #eaeaea;
    padding: 5px 0;
  }

.vc-day {
    padding: 0 5px 3px 5px;
    text-align: left;
    height: 90px;
    background-color: white
  }

  .vc-day {
    padding: 0 5px 3px 5px;
    text-align: left;
    height: 90px;
    background-color: white;
    border: 1px solid #b8c2cc;
    }

 .vc-day-dots {
    margin-bottom: 5px;
  }
</style>